import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Card, Select, Button, Space, Typography,
  message, Tag, Tooltip, DatePicker, Form, Spin, Divider, Grid, Alert, Modal, Checkbox, Row, Col, Input, InputNumber, Statistic, Dropdown, Tabs
} from 'antd';
import { 
  SaveOutlined, ReloadOutlined, InfoCircleOutlined, 
  CheckCircleOutlined, WarningOutlined, ClockCircleOutlined, SyncOutlined,
  PrinterOutlined, FileExcelOutlined, RestOutlined, LockOutlined, UnlockOutlined, EditOutlined,
  SettingOutlined, ExportOutlined, DownOutlined, DollarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { chamCongService } from '../services/chamCongService';
import { masterDataService } from '../services/masterDataService';
import { authService } from '../services/authService';
import { dieuChinhSoDuService, SoDuDto } from '../services/dieuChinhSoDuService';
import { vayHoanCongService, VayHoanCongDto } from '../services/vayHoanCongService';
import { luongService } from '../services/luongService';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

interface ChamCongRow {
  maChamCong: string;
  tenNV: string;
  maKhoa: number;
  thang: number;
  machucvu: number; // New field for role ID
  days: { [key: number]: string };
  secondaryDays: { [key: number]: string };
  realTimes: { [key: number]: any[] };
  tongHuongLuong: number;
  nghiBuLuyKe: number;
  nghiPhepLuyKe: number;
  tongBHXH: number;
  tongNgayDiLam: number;
  tongNgayTruc: number;
  tongNghiCoLuong: number;
  chedoName?: string;
  dailyGiamMinutes: { [key: number]: number };
  dailyChedoColor: { [key: number]: string };
  dailyChedoName: { [key: number]: string };
}

// Utility function placed at top level for shared use
const classifyPunchTimes = (punches: any[], config: any): any => {
  if (!punches || punches.length === 0) return null;
  const p0 = config?.gio_BatDau_Sang || '04:00:00';
  const p1 = config?.moc_VaoRa_Sang || '10:00:00';
  const p2 = config?.moc_KetThuc_Sang || '12:30:00';
  const p3 = config?.moc_BatDau_Chieu || '13:00:00';
  const p4 = config?.moc_VaoRa_Chieu || '15:30:00';
  const p5 = config?.gio_KetThuc_Chieu || '22:00:00';

  let vaoSang: any = null, raSang: any = null, vaoChieu: any = null, raChieu: any = null;
  punches.forEach(p => {
    const time = dayjs(p.gioCham);
    const timeStr = time.format('HH:mm:ss');
    if (timeStr >= p0 && timeStr <= p1) { if (!vaoSang || time.isBefore(vaoSang)) vaoSang = time; }
    else if (timeStr > p1 && timeStr <= p2) { if (!raSang || time.isAfter(raSang)) raSang = time; }
    else if (timeStr >= p3 && timeStr < p4) { if (!vaoChieu || time.isBefore(vaoChieu)) vaoChieu = time; }
    else if (timeStr >= p4 && timeStr <= p5) { if (!raChieu || time.isAfter(raChieu)) raChieu = time; }
  });
  return { vaoSang, raSang, vaoChieu, raChieu };
};

interface TimekeepingCellProps {
  day: number;
  record: ChamCongRow;
  canEdit: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  viewingMonth: dayjs.Dayjs;
  dmLoai: any[];
  dmLoaiPhu: any[];
  cauHinhCas: any[];
  isSuggested: boolean;
  onValueChange: (value: string, maChamCong: string, day: number) => void;
  onSecondaryValueChange: (value: string, maChamCong: string, day: number) => void;
}

const TimekeepingCell = React.memo(({ 
  day, record, canEdit, isEditing, onStartEdit, onEndEdit, viewingMonth, dmLoai, dmLoaiPhu, cauHinhCas, isSuggested,
  onValueChange, onSecondaryValueChange 
}: TimekeepingCellProps) => {
  const value = record.days[day];
  const valuePhu = record.secondaryDays[day];
  const realData = record.realTimes[day] || [];
  
  const activeConfig = useMemo(() => {
    const currentDate = day >= 26 ? viewingMonth.subtract(1, 'month').date(day).startOf('day') : viewingMonth.date(day).startOf('day');
    return [...cauHinhCas]
      .filter(c => {
        const configDate = dayjs(c.ngayApDung).startOf('day');
        return configDate.isSame(currentDate) || configDate.isBefore(currentDate);
      })
      .sort((a, b) => dayjs(b.ngayApDung).diff(dayjs(a.ngayApDung)))[0];
  }, [day, viewingMonth, cauHinhCas]);

  const classified = useMemo(() => classifyPunchTimes(realData, activeConfig), [realData, activeConfig]);
  const loai = useMemo(() => dmLoai.find(l => l.maLoai === value), [dmLoai, value]);
  const loaiPhu = useMemo(() => dmLoaiPhu.find(lp => lp.maLoaiPhu === valuePhu), [dmLoaiPhu, valuePhu]);

  // Cộng dồn số phút được giảm từ: Mã chính + Mã phụ + CHẾ ĐỘ (MỚI)
  const totalGiam = useMemo(() => 
    (loai?.giamGioLamMinutes || 0) + 
    (loaiPhu?.giamGioLamMinutes || 0) + 
    (record.dailyGiamMinutes[day] || 0), 
  [loai, loaiPhu, record.dailyGiamMinutes, day]);

  const chedoColor = record.dailyChedoColor[day];
  let cellBg = chedoColor || (isSuggested ? '#f6ffed' : 'transparent');
  if (!classified && value && loai && loai.heSoCong > 0) cellBg = '#fff1f0';

  const renderPunchTimes = () => {
    if (!classified) return null;
    const { vaoSang, raSang, vaoChieu, raChieu } = classified;
    const renderTimeRange = (inTime: any, outTime: any, inBase: string, outBase: string, giamMinutes = 0) => {
      let inColor = 'inherit', outColor = 'inherit';
      if (activeConfig && value && loai && loai.heSoCong > 0) {
        if (inTime) {
          const baseIn = dayjs(
            inTime.format('YYYY-MM-DD') + ' ' + inBase
          );

          const lateMinutes = inTime.diff(baseIn, 'minute');

          if (lateMinutes > 5) {
            inColor = '#ff4d4f'; // đỏ
          }
          else if (lateMinutes > 0) {
            inColor = '#faad14'; // cam
          }
        }
        if (outTime) {
          const baseOut = dayjs(outTime.format('YYYY-MM-DD') + ' ' + outBase).subtract(giamMinutes, 'minute');
          if (outTime.isBefore(baseOut, 'minute')) outColor = '#faad14';
        }
      }
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 2px' }}>
          <span style={{ color: inColor, fontWeight: inColor !== 'inherit' ? 'bold' : 'normal' }}>{inTime ? inTime.format('HH:mm') : '--:--'}</span>
          <span style={{ color: outColor, fontWeight: outColor !== 'inherit' ? 'bold' : 'normal' }}>{outTime ? outTime.format('HH:mm') : '--:--'}</span>
        </div>
      );
    };

    if (vaoSang && raChieu && !raSang && !vaoChieu) {
      return renderTimeRange(vaoSang, raChieu, activeConfig.gioSang_Vao, activeConfig.gioChieu_Ra, totalGiam);
    }
    return (
      <>
        {(vaoSang || raSang) && renderTimeRange(vaoSang, raSang, activeConfig.gioSang_Vao, activeConfig.gioSang_Ra, totalGiam)}
        {(vaoChieu || raChieu) && renderTimeRange(vaoChieu, raChieu, activeConfig.gioChieu_Vao, activeConfig.gioChieu_Ra, totalGiam)}
      </>
    );
  };

  if (isEditing && canEdit) {
    return (
      <div style={{ backgroundColor: cellBg, padding: '2px', borderRadius: '4px', border: '1px solid #1890ff' }}>
        <div style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
          <Select
            size="small"
            autoFocus
            defaultOpen
            value={value || undefined}
            onChange={(val) => { onValueChange(val, record.maChamCong, day); onEndEdit(); }}
            onBlur={(e) => {
              // Nếu click sang Select bên cạnh trong cùng 1 cell thì không đóng
              if (!e.relatedTarget || !e.currentTarget.parentElement?.contains(e.relatedTarget as Node)) {
                onEndEdit();
              }
            }}
            variant="borderless"
            placeholder="..."
            allowClear
            suffixIcon={null}
            popupMatchSelectWidth={false}
            style={{ flex: 1, textAlign: 'center', fontSize: '10px', backgroundColor: '#fff', borderRadius: '2px' }}
          >
            {dmLoai.map(l => (<Option key={l.maLoai} value={l.maLoai} title={l.tenLoai}>{l.maLoai}</Option>))}
          </Select>
          <Select
            size="small"
            value={valuePhu || undefined}
            onChange={(val) => { onSecondaryValueChange(val, record.maChamCong, day); onEndEdit(); }}
            onBlur={(e) => {
              if (!e.relatedTarget || !e.currentTarget.parentElement?.contains(e.relatedTarget as Node)) {
                onEndEdit();
              }
            }}
            variant="borderless"
            placeholder="+"
            allowClear
            suffixIcon={null}
            popupMatchSelectWidth={false}
            style={{ width: '35px', textAlign: 'center', fontSize: '9px', color: '#1890ff', backgroundColor: '#fff', borderRadius: '2px' }}
          >
            {dmLoaiPhu.map(lp => (<Option key={lp.maLoaiPhu} value={lp.maLoaiPhu} title={lp.tenLoaiPhu}>{lp.maLoaiPhu}</Option>))}
          </Select>
        </div>
        {renderPunchTimes()}
      </div>
    );
  }

  return (
    <div 
      onClick={() => canEdit && onStartEdit()}
      style={{ 
        backgroundColor: cellBg, padding: '2px', borderRadius: '4px', fontSize: '11px', lineHeight: '1.2', 
        border: isSuggested ? '1px dashed #b7eb8f' : '1px solid transparent',
        cursor: canEdit ? 'pointer' : 'default',
        minHeight: '38px'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', marginBottom: '2px', fontWeight: 'bold' }}>
        <span style={{ color: value ? 'inherit' : '#bfbfbf' }}>{value || '...'}</span>
        {valuePhu && <span style={{ color: '#1890ff' }}>/{valuePhu}</span>}
      </div>
      {renderPunchTimes()}
    </div>
  );
}, (prev, next) => {
  return prev.isEditing === next.isEditing &&
         prev.canEdit === next.canEdit &&
         prev.record.days[prev.day] === next.record.days[next.day] &&
         prev.record.secondaryDays[prev.day] === next.record.secondaryDays[next.day] &&
         prev.isSuggested === next.isSuggested &&
         prev.viewingMonth.isSame(next.viewingMonth, 'month') &&
         prev.cauHinhCas === next.cauHinhCas;
});

const ChamCongKhoaPhongTab: React.FC = () => {
  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ChamCongRow[]>([]);
  const [dmLoai, setDmLoai] = useState<any[]>([]);
  const [dmLoaiPhu, setDmLoaiPhu] = useState<any[]>([]);
  const [dmChedo, setDmChedo] = useState<any[]>([]);
  const [khoaPhongs, setKhoaPhongs] = useState<any[]>([]);
  const [cauHinhCas, setCauHinhCas] = useState<any[]>([]);
  
  const defaultMonth = useMemo(() => {
    const now = dayjs();
    return now.date() >= 26 ? now.add(1, 'month') : now;
  }, []);

  const [viewingMonth, setViewingMonth] = useState<dayjs.Dayjs>(defaultMonth);
  const [isManualUnlocked, setIsManualUnlocked] = useState(false);
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [suggestedCells, setSuggestedCells] = useState<Set<string>>(new Set());
  
  const [loadedContext, setLoadedContext] = useState<{ maKhoa: number | null, month: dayjs.Dayjs | null }>({
    maKhoa: null,
    month: null
  });

  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetForm] = Form.useForm();

  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncEmployees, setSyncEmployees] = useState<any[]>([]);
  const [syncForm] = Form.useForm();

  const [chedoModalVisible, setChedoModalVisible] = useState(false);
  const [chedoLoading, setChedoLoading] = useState(false);
  const [currentNhanVienChedos, setCurrentNhanVienChedos] = useState<any[]>([]);
  const [chedoForm] = Form.useForm();
  const chedoIsContinuous = Form.useWatch('isContinuous', chedoForm);
  const chedoMaChamCong = Form.useWatch('maChamCong', chedoForm);

  useEffect(() => {
    if (chedoModalVisible && chedoMaChamCong && viewingMonth) {
      loadCurrentNhanVienChedos(chedoMaChamCong);
    } else {
      setCurrentNhanVienChedos([]);
    }
  }, [chedoModalVisible, chedoMaChamCong, viewingMonth]);

  const loadCurrentNhanVienChedos = async (macc: string) => {
    try {
      const tuNgay = viewingMonth.subtract(1, 'month').date(26).format('YYYY-MM-DD');
      const denNgay = viewingMonth.date(25).format('YYYY-MM-DD');
      const res = await chamCongService.getNhanVienChedos(macc, tuNgay, denNgay);
      setCurrentNhanVienChedos(res.data || []);
    } catch (error) {
      console.error('Lỗi khi tải chế độ hiện tại:', error);
    }
  };

  const handleEditChedo = (record: any) => {
    chedoForm.setFieldsValue({
      id: record.id,
      maChamCong: record.maChamCong,
      maChedo: record.maChedo,
      isContinuous: record.isContinuous,
      range: record.isContinuous ? [dayjs(record.tuNgay), dayjs(record.denNgay)] : undefined,
      discreteDates: !record.isContinuous ? (record.details || []).map((d: any) => dayjs(d.ngayApDung)) : undefined,
      ghiChu: record.ghiChu
    });
  };

  const handleDeleteChedo = async (id: number) => {
    modal.confirm({
      title: 'Xác nhận xóa',
      content: 'Bạn có chắc chắn muốn xóa chế độ này không?',
      onOk: async () => {
        try {
          await chamCongService.deleteNhanVienChedo(id);
          message.success('Xóa chế độ thành công');
          if (chedoMaChamCong) loadCurrentNhanVienChedos(chedoMaChamCong);
          if (loadedContext.maKhoa && loadedContext.month) {
            fetchData(loadedContext.maKhoa, loadedContext.month);
          }
        } catch (error) {
          message.error('Lỗi khi xóa chế độ');
        }
      }
    });
  };

  const [userMaKK, setUserMaKK] = useState<number | null>(null);
  const [canViewAll, setCanViewAll] = useState(false);
  const [canEditAll, setCanEditAll] = useState(false);
  const [hasConfigPerm, setHasConfigPerm] = useState(false);
  const [hasDieuChinhBuPhepPerm, setHasDieuChinhBuPhepPerm] = useState(false);
  const [hasTinhLuongPerm, setHasTinhLuongPerm] = useState(false);

  // State cho tính lương
  const [activeTabKey, setActiveTabKey] = useState('chamcong');
  const [bangLuongData, setBangLuongData] = useState<any[]>([]);
  const [luongLoading, setLuongLoading] = useState(false);
  const [luongNhanVienModalVisible, setLuongNhanVienModalVisible] = useState(false);
  const [selectedEmployeeForLuong, setSelectedEmployeeForLuong] = useState<string | null>(null);
  const [luongNhanVienForm] = Form.useForm();
  const [luongNhanVienList, setLuongNhanVienList] = useState<any[]>([]);
  const [luongNhanVienFilterName, setLuongNhanVienFilterName] = useState<string>('');
  const [luongNhanVienPagination, setLuongNhanVienPagination] = useState({ current: 1, pageSize: 20 });
  const [luongConfigModalVisible, setLuongConfigModalVisible] = useState(false);
  const [luongConfigForm] = Form.useForm();
  const [luongConfigData, setLuongConfigData] = useState<any>(null);
  const [tienPhuCapTrucModalVisible, setTienPhuCapTrucModalVisible] = useState(false);
  const [tienPhuCapTrucForm] = Form.useForm();
  const [tienPhuCapTrucList, setTienPhuCapTrucList] = useState<any[]>([]);
  const [ngoaiLeTienTrucModalVisible, setNgoaiLeTienTrucModalVisible] = useState(false);
  const [ngoaiLeTienTrucForm] = Form.useForm();
  const [ngoaiLeTienTrucList, setNgoaiLeTienTrucList] = useState<any[]>([]);
  
  // State cho modal điều chỉnh nghỉ bù/phép
  const [dieuChinhModalVisible, setDieuChinhModalVisible] = useState(false);
  const [dieuChinhModalLoading, setDieuChinhModalLoading] = useState(false);
  const [selectedEmployeeForDieuChinh, setSelectedEmployeeForDieuChinh] = useState<string | null>(null);
  const [soDuForDieuChinh, setSoDuForDieuChinh] = useState<SoDuDto | null>(null);
  const [loaiNgayForDieuChinh, setLoaiNgayForDieuChinh] = useState<'NghiBu' | 'NghiPhep'>('NghiBu');
  const [soNgayForDieuChinh, setSoNgayForDieuChinh] = useState<number | null>(0);
  const [lyDoForDieuChinh, setLyDoForDieuChinh] = useState('');
  
  // State cho modal Vay Hoàn Công
  const [vayHoanModalVisible, setVayHoanModalVisible] = useState(false);
  const [vayHoanModalLoading, setVayHoanModalLoading] = useState(false);
  const [selectedEmployeeForVayHoan, setSelectedEmployeeForVayHoan] = useState<string | null>(null);
  const [vayHoanCurrentRecord, setVayHoanCurrentRecord] = useState<VayHoanCongDto | null>(null);
  const [vayHoanTuThang, setVayHoanTuThang] = useState<dayjs.Dayjs | null>(null);
  const [vayHoanSoNgay, setVayHoanSoNgay] = useState<number | null>(1);
  const [vayHoanLyDo, setVayHoanLyDo] = useState('');
  const vayHoanDenThang = useMemo(() => vayHoanTuThang ? vayHoanTuThang.add(1, 'month') : null, [vayHoanTuThang]);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const formMaKhoa = Form.useWatch('maKhoa', form);
  const formMonth = Form.useWatch('month', form);

  // Quyền chỉnh sửa cho khoa đang chọn
  const canEditCurrent = useMemo(() => {
    if (canEditAll) return true;
    return formMaKhoa === userMaKK;
  }, [canEditAll, formMaKhoa, userMaKK]);

  const isPastMonth = useMemo(() => {
    if (!formMonth) return false;
    return formMonth.isBefore(defaultMonth, 'month');
  }, [formMonth, defaultMonth]);

  // Quyền chỉnh sửa thực tế trên ô (bao gồm cả kiểm tra khóa tháng cũ)
  const isCellEditable = useMemo(() => {
    if (!canEditCurrent) return false;
    if (isPastMonth && !isManualUnlocked) return false;
    return true;
  }, [canEditCurrent, isPastMonth, isManualUnlocked]);

  const isDataOutdated = useMemo(() => {
    if (!loadedContext.maKhoa || !loadedContext.month || !formMaKhoa || !formMonth) return true;
    return formMaKhoa !== loadedContext.maKhoa || !formMonth.isSame(loadedContext.month, 'month');
  }, [formMaKhoa, formMonth, loadedContext]);

  useEffect(() => {
    setIsManualUnlocked(false);
  }, [formMonth]);

  const loadInitialData = useCallback(async () => {
    try {
      const [loaiRes, loaiPhuRes, chedoRes, khoaRes, userRes] = await Promise.all([
        chamCongService.getDanhMucLoai(),
        chamCongService.getDanhMucLoaiPhu(),
        chamCongService.getDanhMucChedo(),
        masterDataService.getDepartments(),
        authService.getProfile()
      ]);
      
      setDmLoai(loaiRes.data);
      setDmLoaiPhu(loaiPhuRes.data);
      setDmChedo(chedoRes.data);
      
      const userPermissions = userRes.permissions || [];
      const viewAll = userPermissions.includes('CCTatCaKhoa') || userPermissions.includes('CCConfig');
      const editAll = userPermissions.includes('CCConfig');
      const config = userPermissions.includes('CCConfig');
      const dieuChinhBuPhep = userPermissions.includes('DCbuphep');
      const tinhLuong = userPermissions.includes('TinhLuong');

      setUserMaKK(userRes.maKK);
      setCanViewAll(viewAll);
      setCanEditAll(editAll);
      setHasConfigPerm(config);
      setHasDieuChinhBuPhepPerm(dieuChinhBuPhep);
      setHasTinhLuongPerm(tinhLuong);
      
      let filteredKhoas = khoaRes.data;
      if (!viewAll) {
        filteredKhoas = khoaRes.data.filter((k: any) => k.makk === userRes.maKK);
      }
      setKhoaPhongs(filteredKhoas);
      
      if (filteredKhoas.length > 0) {
        const defaultMaKhoa = filteredKhoas.length === 1 ? filteredKhoas[0].makk : (viewAll ? undefined : userRes.maKK);
        form.setFieldsValue({ 
          maKhoa: defaultMaKhoa,
          month: defaultMonth
        });
      }
    } catch (error) {
      message.error('Lỗi khi tải danh mục');
    }
  }, [form, defaultMonth]);

  const fetchData = async (maKhoa: number, month: dayjs.Dayjs) => {
    setLoading(true);
    setViewingMonth(month);
    try {
      const [bangCongRes, caRes] = await Promise.all([
        chamCongService.getBangCong(maKhoa, month.month() + 1, month.year()),
        chamCongService.getCauHinhCa(maKhoa)
      ]);
      setData(bangCongRes.data);
      setCauHinhCas(caRes.data || []);
      setLoadedContext({ maKhoa, month });
    } catch (error) {
      message.error('Lỗi khi tải bảng công');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleSearch = (values: any) => {
    setSuggestedCells(new Set());
    fetchData(values.maKhoa, values.month);
  };

  const handleSuggestAutoFill = async () => {
    const { maKhoa, month } = loadedContext;
    if (!maKhoa || !month) {
      message.warning('Vui lòng tải dữ liệu trước');
      return;
    }

    setLoading(true);
    try {
      const res = await chamCongService.getSuggestions(maKhoa, month.month() + 1, month.year());
      const suggestions = res.data;
      
      const newData = [...data];
      const newSuggestedCells = new Set<string>();
      let fillCount = 0;

      suggestions.forEach((item: any) => {
        const rowIndex = newData.findIndex(r => r.maChamCong === item.maChamCong);
        if (rowIndex > -1) {
          Object.keys(item.suggestions).forEach((dayStr: string) => {
            const day = parseInt(dayStr);
            if (!newData[rowIndex].days[day]) {
              newData[rowIndex].days[day] = item.suggestions[day];
              newSuggestedCells.add(`${item.maChamCong}-${day}`);
              fillCount++;
            }
          });
        }
      });

      setData(newData);
      setSuggestedCells(newSuggestedCells);
      message.success(`Đã gợi ý tự động ${fillCount} ô công dựa trên vân tay và lịch trực`);
    } catch (error) {
      message.error('Lỗi khi lấy gợi ý công');
    } finally {
      setLoading(false);
    }
  };

  const handleResetInit = async (values: any) => {
    setResetLoading(true);
    try {
      const { maChamCongs, ngayBatDau, isDefault } = values;
      await chamCongService.resetInit({
        maChamCongs: maChamCongs || [],
        ngayBatDau: ngayBatDau.format('YYYY-MM-DD'),
        isDefault: !!isDefault
      });
      message.success('Reset số dư khởi tạo thành công');
      setResetModalVisible(false);
      if (loadedContext.maKhoa && loadedContext.month) {
        fetchData(loadedContext.maKhoa, loadedContext.month);
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Lỗi khi reset số dư');
    } finally {
      setResetLoading(false);
    }
  };
  
  // Xử lý chọn nhân viên cho điều chỉnh nghỉ bù/phép
  const handleDieuChinhEmployeeChange = async (maChamCong: string) => {
    setSelectedEmployeeForDieuChinh(maChamCong);
    setDieuChinhModalLoading(true);
    try {
      const soDuRes = await dieuChinhSoDuService.getSoDu(maChamCong);
      setSoDuForDieuChinh(soDuRes);
    } catch (error) {
      console.error('Error loading so du:', error);
      message.error('Lỗi khi tải thông tin số dư');
    } finally {
      setDieuChinhModalLoading(false);
    }
  };
  
  // Xử lý lưu điều chỉnh nghỉ bù/phép
  const handleDieuChinhSave = async () => {
    if (!selectedEmployeeForDieuChinh) {
      message.error('Vui lòng chọn nhân viên');
      return;
    }
    if (!lyDoForDieuChinh || lyDoForDieuChinh.trim() === '') {
      message.error('Vui lòng nhập lý do');
      return;
    }
    if (soNgayForDieuChinh === null) {
      message.error('Vui lòng nhập số ngày');
      return;
    }

    setDieuChinhModalLoading(true);
    try {
      await dieuChinhSoDuService.capNhatSoDu({
        maChamCongNhanVien: selectedEmployeeForDieuChinh,
        loaiNgay: loaiNgayForDieuChinh,
        soNgay: soNgayForDieuChinh,
        lyDo: lyDoForDieuChinh
      });
      message.success('Cập nhật thành công!');
      // Refresh số dư
      if (selectedEmployeeForDieuChinh) {
        const soDuRes = await dieuChinhSoDuService.getSoDu(selectedEmployeeForDieuChinh);
        setSoDuForDieuChinh(soDuRes);
      }
      // Refresh bảng công để cập nhật số dư lũy kế
      if (loadedContext.maKhoa && loadedContext.month) {
        fetchData(loadedContext.maKhoa, loadedContext.month);
      }
      // Reset form
      setSoNgayForDieuChinh(0);
    } catch (error) {
      console.error('Error updating so du:', error);
      message.error('Lỗi khi cập nhật số dư');
    } finally {
      setDieuChinhModalLoading(false);
    }
  };

  // ============ Xử lý Vay Hoàn Công ============
  const handleVayHoanEmployeeChange = async (maChamCong: string) => {
    setSelectedEmployeeForVayHoan(maChamCong);
    setVayHoanCurrentRecord(null);
    // Nếu đã chọn từ tháng, tự động kiểm tra xem kỳ đó đã có liên hệ chưa
    if (maChamCong && vayHoanTuThang) {
      setVayHoanModalLoading(true);
      try {
        const record = await vayHoanCongService.getByKy(maChamCong, vayHoanTuThang.month() + 1, vayHoanTuThang.year());
        if (record) {
          setVayHoanCurrentRecord(record);
          // Đổ dữ liệu cũ vào form
          if (record.thangNguon === vayHoanTuThang.month() + 1 && record.namNguon === vayHoanTuThang.year()) {
            // Vai trò nguồn: kỳ được chọn là nguồn
            setVayHoanSoNgay(record.soNgayDieuChinh);
          } else {
            // Vai trò đích: kỳ được chọn là đích (nghĩa là nguồn là kỳ trước đó)
            // Tự động set lại từ tháng = kỳ nguồn cũ để thẳng hàng
            setVayHoanTuThang(dayjs(new Date(record.namNguon, record.thangNguon - 1, 1)));
            setVayHoanSoNgay(record.soNgayDieuChinh);
          }
          setVayHoanLyDo(record.lyDo || '');
        }
      } catch (error) {
        console.error('Lỗi khi tải liên hệ vay-hoàn cũ:', error);
      } finally {
        setVayHoanModalLoading(false);
      }
    }
  };

  const handleVayHoanTuThangChange = async (value: dayjs.Dayjs | null) => {
    setVayHoanTuThang(value);
    setVayHoanCurrentRecord(null);
    // Nếu đã chọn nhân viên, tự động kiểm tra liên hệ cũ
    if (value && selectedEmployeeForVayHoan) {
      setVayHoanModalLoading(true);
      try {
        const record = await vayHoanCongService.getByKy(selectedEmployeeForVayHoan, value.month() + 1, value.year());
        if (record) {
          setVayHoanCurrentRecord(record);
          if (record.thangNguon === value.month() + 1 && record.namNguon === value.year()) {
            setVayHoanSoNgay(record.soNgayDieuChinh);
          } else {
            // Kỳ đang chọn là ĐÍCH → set lại từ tháng thành kỳ NGUỒN cũ
            setVayHoanTuThang(dayjs(new Date(record.namNguon, record.thangNguon - 1, 1)));
            setVayHoanSoNgay(record.soNgayDieuChinh);
          }
          setVayHoanLyDo(record.lyDo || '');
        }
      } catch (error) {
        console.error('Lỗi khi tải liên hệ vay-hoàn cũ:', error);
      } finally {
        setVayHoanModalLoading(false);
      }
    }
  };

  const handleVayHoanSave = async () => {
    if (!selectedEmployeeForVayHoan) {
      message.error('Vui lòng chọn nhân viên');
      return;
    }
    if (!vayHoanTuThang) {
      message.error('Vui lòng chọn Từ tháng (kỳ nguồn)');
      return;
    }
    if (!vayHoanDenThang) {
      message.error('Đến tháng không hợp lệ');
      return;
    }
    if (vayHoanSoNgay === null || vayHoanSoNgay === undefined) {
      message.error('Vui lòng nhập số ngày điều chỉnh');
      return;
    }
    if (Math.abs(vayHoanSoNgay) > 2) {
      message.error('Số ngày điều chỉnh không được vượt quá ±2 ngày');
      return;
    }
    if (!vayHoanLyDo || vayHoanLyDo.trim() === '') {
      message.error('Vui lòng nhập lý do');
      return;
    }

    setVayHoanModalLoading(true);
    try {
      await vayHoanCongService.luu({
        id: vayHoanCurrentRecord?.id ?? null,
        maChamCong: selectedEmployeeForVayHoan,
        thangNguon: vayHoanTuThang.month() + 1,
        namNguon: vayHoanTuThang.year(),
        thangDich: vayHoanDenThang.month() + 1,
        namDich: vayHoanDenThang.year(),
        soNgayDieuChinh: vayHoanSoNgay,
        lyDo: vayHoanLyDo
      });
      message.success(vayHoanCurrentRecord ? 'Cập nhật liên hệ vay-hoàn công thành công' : 'Tạo liên hệ vay-hoàn công thành công');
      setVayHoanModalVisible(false);
      // Reset form
      setSelectedEmployeeForVayHoan(null);
      setVayHoanCurrentRecord(null);
      setVayHoanTuThang(null);
      setVayHoanSoNgay(1);
      setVayHoanLyDo('');
      // Refresh bảng công nếu có ngữ cảnh tải
      if (loadedContext.maKhoa && loadedContext.month) {
        fetchData(loadedContext.maKhoa, loadedContext.month);
      }
    } catch (error: any) {
      console.error('Lỗi khi lưu vay-hoàn công:', error);
      message.error(error.response?.data?.message || 'Lỗi khi lưu vay-hoàn công');
    } finally {
      setVayHoanModalLoading(false);
    }
  };

  const loadSyncEmployees = async (maKhoa: number) => {
    try {
      const res = await masterDataService.getEmployeesByDepartment(maKhoa);
      setSyncEmployees(res.data);
    } catch (error) {
      console.error('Error loading sync employees:', error);
    }
  };

  const handleCellChange = useCallback((value: string, maChamCong: string, day: number) => {
    setData(prevData => {
      const newData = [...prevData];
      const index = newData.findIndex(item => item.maChamCong === maChamCong);
      if (index > -1) {
        newData[index] = {
          ...newData[index],
          days: { ...newData[index].days, [day]: value }
        };
      }
      return newData;
    });
  }, []);

  const handleSecondaryCellChange = useCallback((value: string, maChamCong: string, day: number) => {
    setData(prevData => {
      const newData = [...prevData];
      const index = newData.findIndex(item => item.maChamCong === maChamCong);
      if (index > -1) {
        newData[index] = {
          ...newData[index],
          secondaryDays: { ...newData[index].secondaryDays, [day]: value }
        };
      }
      return newData;
    });
  }, []);

  const handleSave = async () => {
    if (isDataOutdated) {
      message.error('Dữ liệu không khớp với lựa chọn hiện tại. Vui lòng bấm "Lấy dữ liệu" trước khi lưu.');
      return;
    }

    setLoading(true);
    try {
      const month = loadedContext.month!;
      const maKhoa = loadedContext.maKhoa!;
      const thang = month.month() + 1;
      const nam = month.year();
      const prevMonthDate = month.subtract(1, 'month');
      const prevThang = prevMonthDate.month() + 1;
      const prevNam = prevMonthDate.year();

      for (const row of data) {
        const payloadPrev: any = { MaChamCong: row.maChamCong, MaKhoa: maKhoa, Thang: prevThang, Nam: prevNam };
        const payloadPhuPrev: any = { ...payloadPrev };
        const daysInPrev = prevMonthDate.daysInMonth();

        for (let i = 26; i <= daysInPrev; i++) {
          payloadPrev[`D${i}`] = row.days[i] || "";
          payloadPhuPrev[`D${i}`] = row.secondaryDays[i] || "";
        }
        await chamCongService.saveBangCong(payloadPrev);
        await chamCongService.saveBangPhu(payloadPhuPrev);

        const payloadCurr: any = { MaChamCong: row.maChamCong, MaKhoa: maKhoa, Thang: thang, Nam: nam };
        const payloadPhuCurr: any = { ...payloadCurr };

        for (let i = 1; i <= 25; i++) {
          payloadCurr[`D${i}`] = row.days[i] || "";
          payloadPhuCurr[`D${i}`] = row.secondaryDays[i] || "";
        }
        await chamCongService.saveBangCong(payloadCurr);
        await chamCongService.saveBangPhu(payloadPhuCurr);
      }

      message.success(`Đã lưu thành công khoa ${khoaPhongs.find(k => k.makk === maKhoa)?.tenkk} tháng ${month.format('MM/YYYY')}`);
      fetchData(maKhoa, month);
    } catch (error) {
      message.error('Lỗi khi lưu dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromHis = async (values: any) => {
    setSyncLoading(true);
    try {
      const { month, maKhoa } = loadedContext;
      const { maChamCongs } = values;
      if (!month || !maKhoa) {
        message.error('Không xác định được ngữ cảnh đồng bộ. Vui lòng tải dữ liệu trước.');
        return;
      }
      await chamCongService.syncFromHis({
        thang: month.month() + 1,
        nam: month.year(),
        maKhoa: maKhoa,
        maChamCongs: maChamCongs || []
      });
      message.success('Đồng bộ dữ liệu từ HIS thành công');
      setSyncModalVisible(false);
      fetchData(maKhoa, month);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Lỗi khi đồng bộ dữ liệu');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSyncAllEmployees = async () => {
    // ... (unchanged code)
  };

  const handleSaveChedo = async (values: any) => {
    setChedoLoading(true);
    try {
      const { maChamCong, maChedo, range, isContinuous, discreteDates, ghiChu } = values;
      const payload = {
        MaChamCong: maChamCong,
        MaChedo: maChedo,
        IsContinuous: !!isContinuous,
        GhiChu: ghiChu,
        TuNgay: isContinuous ? range[0].format('YYYY-MM-DD') : dayjs().startOf('year').format('YYYY-MM-DD'),
        DenNgay: isContinuous ? range[1].format('YYYY-MM-DD') : dayjs().endOf('year').format('YYYY-MM-DD'),
        Details: !isContinuous && discreteDates ? discreteDates.map((d: any) => ({ NgayApDung: d.format('YYYY-MM-DD') })) : []
      };
      await chamCongService.saveNhanVienChedo(payload);
      message.success('Lưu chế độ nhân viên thành công');
      setChedoModalVisible(false);
      if (loadedContext.maKhoa && loadedContext.month) {
        fetchData(loadedContext.maKhoa, loadedContext.month);
      }
    } catch (error: any) {
      const details = error.response?.data?.details;
      const errorMsg = Array.isArray(details) ? details.join(', ') : (details || error.response?.data?.error || 'Lỗi khi lưu chế độ');
      message.error(errorMsg);
    } finally {
      setChedoLoading(false);
    }
  };

  const generateTableHtml = (detailed = false, isExport = false, customData?: ChamCongRow[], simplified = false, isGroupExport = false) => {
    const dataSource = customData || data;
    const month = form.getFieldValue('month');
    const maKhoa = form.getFieldValue('maKhoa');
    const khoaName = customData ? 'TOÀN VIỆN' : (khoaPhongs.find(k => k.makk === maKhoa)?.tenkk || '');
    const prevMonthDate = month.subtract(1, 'month');
    const daysInPrevMonth = prevMonthDate.daysInMonth();
    
    let html = `
      <style>
        table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; font-size: ${detailed ? '10px' : '11px'}; }
        th, td { border: 1px solid black; padding: 2px; text-align: center; vertical-align: top; }
        th { background-color: #f2f2f2; }
        .col-macc { min-width: 35px; font-size: 9px; }
        .col-name { min-width: 140px; text-align: left; white-space: nowrap; }
        .col-day { min-width: ${detailed ? '50px' : '22px'}; }
        .col-summary { min-width: 30px; font-weight: bold; }
        .no-border, .no-border td { border: none !important; }
        .text-left { text-align: left; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .bold { font-weight: bold; }
        .header-info { margin-bottom: 10px; text-align: center; }
        .signature-space { height: 60px; }
        @media print { @page { size: landscape; margin: 5mm; } }
      </style>`;

    if (!isExport) {
      html += `
      <table class="no-border" style="margin-bottom: 10px;">
        <tr>
          <td class="text-center" style="width: 40%;">
            <div class="bold">TẬP ĐOÀN MƯỜNG THANH</div>
            <div class="bold">BỆNH VIỆN ĐA KHOA PHỦ DIỄN</div>
            <div>-----------------------</div>
          </td>
          <td style="width: 20%;"></td>
          <td class="text-center" style="width: 40%;">
            <div class="bold">Mẫu số C01a-HD</div>
            <div>(Ban hành kèm theo TT 185/2010/TT-BTC ngày 15/11/2011 BTC)</div>
            <div>Mã số: [........]</div>
          </td>
        </tr>
      </table>`;
    }

    html += `
      <div class="header-info">
        <h2 style="margin: 5px 0;">BẢNG CHẤM CÔNG KHOA PHÒNG ${isGroupExport ? 'TẬP ĐOÀN' : (detailed ? 'CHI TIẾT' : '')}</h2>
        <div class="bold">Khoa: ${khoaName} - Tháng: ${month.format('MM/YYYY')}</div>
        <div style="font-style: italic;">(Chu kỳ từ ngày 26/${prevMonthDate.format('MM/YYYY')} đến ngày 25/${month.format('MM/YYYY')})</div>
      </div>
      <table>
        <thead>
          <tr>
            <th rowspan="2" class="col-macc">Mã CC</th>
            <th rowspan="2" class="col-name">Họ tên</th>
    `;

    for (let i = 26; i <= daysInPrevMonth; i++) html += `<th class="col-day">${i}</th>`;
    for (let i = 1; i <= 25; i++) html += `<th class="col-day">${i}</th>`;
    
    html += `
            <th rowspan="2" class="col-summary">Đi làm</th>
            <th rowspan="2" class="col-summary">Hưởng lương</th>
            <th rowspan="2" class="col-summary">BHXH</th>
            <th rowspan="2" class="col-summary">Trực</th>
            <th rowspan="2" class="col-summary">Nghỉ CL</th>
            <th rowspan="2" class="col-summary">Tồn nghỉ bù</th>
            <th rowspan="2" class="col-summary">Tồn phép</th>
          </tr>
          <tr>
    `;

    const dayLabels = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
    for (let i = 26; i <= daysInPrevMonth; i++) html += `<td class="col-day">${dayLabels[prevMonthDate.date(i).day()]}</td>`;
    for (let i = 1; i <= 25; i++) html += `<td class="col-day">${dayLabels[month.date(i).day()]}</td>`;
    
    html += `</tr></thead><tbody>`;
    const bgd = ['172', '215', '55', '76'];
    dataSource.forEach(row => {
      html += `<tr><td class="col-macc">${row.maChamCong}</td><td class="col-name">${row.tenNV}</td>`;
      const renderExcelCell = (day: number) => {
        const showColors = !isExport || (detailed && !customData);
        let val = row.days[day] || '';
           if ((isGroupExport || isExport) && (val === 'T1' || val === 'T2')) {
      if (bgd.includes(row.maChamCong)) {
        val = 'T';
      } else {
        val = 'tr';
      }
    }
        if (isGroupExport && (val === 'P+')) {
          val = 'P';
        }
        if (isGroupExport && (val === 'Nx')) {
          val = 'Nb';
        }
        if (isGroupExport && ((val === 'CB') || (val === 'CP'))) {
          val = 'CN';
        }
        if (isGroupExport && ((val === 'CS') || (val === 'CC'))) {
          val = 'Ng';
        }
        const valPhu = row.secondaryDays[day] || '';
        const chedoName = row.dailyChedoName[day];
        
        let displayVal = (simplified || !valPhu) ? val : `${val}/${valPhu}`;
        if (chedoName && (detailed || customData)) {
          displayVal = `${displayVal}${displayVal ? '<br/>' : ''}<span style="font-size: 12px; font-weight: normal; color: #000000;">(${chedoName})</span>`;
        }

        if (!detailed) return `<td>${displayVal}</td>`;

        const realData = row.realTimes[day] || [];
        const loai = dmLoai.find(l => l.maLoai === val);
        const loaiPhu = dmLoaiPhu.find(lp => lp.maLoaiPhu === valPhu);
        const totalGiam = (loai?.giamGioLamMinutes || 0) + (loaiPhu?.giamGioLamMinutes || 0) + (row.dailyGiamMinutes[day] || 0);

        const currentDate = day >= 26 ? prevMonthDate.date(day).startOf('day') : month.date(day).startOf('day');

        const activeConfig = [...cauHinhCas]
          .filter(c => {
            const configDate = dayjs(c.ngayApDung).startOf('day');
            return configDate.isSame(currentDate) || configDate.isBefore(currentDate);
          })
          .sort((a, b) => dayjs(b.ngayApDung).diff(dayjs(a.ngayApDung)))[0];

        const chedoColor = row.dailyChedoColor[day];
        let cellStyle = '', morningRow = '', afternoonRow = '';
        const classified = classifyPunchTimes(realData, activeConfig);

        if (classified) {
          const { vaoSang, raSang, vaoChieu, raChieu } = classified;
          
          const getLateEarlyColors = (inTime: any, outTime: any, inBase: string, outBase: string, giamMin = 0) => {
            let inColor = '', outColor = '';
            if (showColors && activeConfig && val && loai && loai.heSoCong > 0) {
              if (inTime) {
                const baseIn = dayjs(inTime.format('YYYY-MM-DD') + ' ' + inBase);
                const lateMin = inTime.diff(baseIn, 'minute');
                if (lateMin > 5) inColor = 'color: #ff4d4f; font-weight: bold;';
                else if (lateMin > 0) inColor = 'color: #faad14; font-weight: bold;';
              }
              if (outTime) {
                const baseOut = dayjs(outTime.format('YYYY-MM-DD') + ' ' + outBase).subtract(giamMin, 'minute');
                if (outTime.isBefore(baseOut, 'minute')) outColor = 'color: #faad14; font-weight: bold;';
              }
            }
            return { inStyle: inColor, outStyle: outColor };
          };

          if (vaoSang && raChieu && !raSang && !vaoChieu) {
            const { inStyle, outStyle } = getLateEarlyColors(vaoSang, raChieu, activeConfig.gioSang_Vao, activeConfig.gioChieu_Ra, totalGiam);
            morningRow = `<div style="font-size: 10px;"><span style="${inStyle}">${vaoSang.format('HH:mm')}</span>-<span style="${outStyle}">${raChieu.format('HH:mm')}</span></div>`;
          } else {
            if (vaoSang || raSang) {
              const { inStyle, outStyle } = getLateEarlyColors(vaoSang, raSang, activeConfig.gioSang_Vao, activeConfig.gioSang_Ra, totalGiam);
              morningRow = `<div style="font-size: 10px;"><span style="${inStyle}">${vaoSang ? vaoSang.format('HH:mm') : '--:--'}</span>-<span style="${outStyle}">${raSang ? raSang.format('HH:mm') : '--:--'}</span></div>`;
            }
            if (vaoChieu || raChieu) {
              const { inStyle, outStyle } = getLateEarlyColors(vaoChieu, raChieu, activeConfig.gioChieu_Vao, activeConfig.gioChieu_Ra, totalGiam);
              afternoonRow = `<div style="font-size: 10px;"><span style="${inStyle}">${vaoChieu ? vaoChieu.format('HH:mm') : '--:--'}</span>-<span style="${outStyle}">${raChieu ? raChieu.format('HH:mm') : '--:--'}</span></div>`;
            }
          }
        } else if (showColors && val && loai && loai.heSoCong > 0) {
          cellStyle = 'background-color: #fff1f0;';
        }

        if (showColors && chedoColor) {
          cellStyle = `background-color: ${chedoColor};`;
        }

        return `<td class="col-day" style="${cellStyle}"><div style="font-weight: bold;">${displayVal}</div>${morningRow}${afternoonRow}</td>`;
      };

      for (let i = 26; i <= daysInPrevMonth; i++) html += renderExcelCell(i);
      for (let i = 1; i <= 25; i++) html += renderExcelCell(i);
      
      html += `<td class="col-summary">${row.tongNgayDiLam}</td><td class="col-summary"><b>${row.tongHuongLuong}</b></td><td class="col-summary">${row.tongBHXH}</td><td class="col-summary">${row.tongNgayTruc}</td><td class="col-summary">${row.tongNghiCoLuong}</td><td class="col-summary">${row.nghiBuLuyKe}</td><td class="col-summary">${row.nghiPhepLuyKe}</td></tr>`;
    });

    html += `</tbody></table>`;

    if (!isExport) {
      html += `
      <table class="no-border" style="margin-top: 20px;">
        <tr><td colspan="4" class="text-right" style="font-style: italic; padding-bottom: 10px;">Ngày ...... tháng ...... năm .........</td></tr>
        <tr>
          <td class="text-center bold" style="width: 25%;">THỦ TRƯỞNG ĐƠN VỊ</td>
          <td class="text-center bold" style="width: 25%;">PHÒNG TCHC</td>
          <td class="text-center bold" style="width: 25%;">PHỤ TRÁCH BỘ PHẬN</td>
          <td class="text-center bold" style="width: 25%;">NGƯỜI CHẤM CÔNG</td>
        </tr>
        <tr>
          <td class="text-center">(Ký và ghi rõ họ tên)</td>
          <td class="text-center">(Ký và ghi rõ họ tên)</td>
          <td class="text-center">(Ký và ghi rõ họ tên)</td>
          <td class="text-center">(Ký và ghi rõ họ tên)</td>
        </tr>
        <tr><td class="signature-space"></td><td class="signature-space"></td><td class="signature-space"></td><td class="signature-space"></td></tr>
      </table>`;
    }
    return html;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const html = generateTableHtml(false, false, undefined, true);
    printWindow.document.write(`<html><head><title>In bảng công</title></head><body>${html}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
  };

  const handleExportExcel = (detailed = false) => {
    const html = generateTableHtml(detailed, true, undefined, !detailed);
    const month = form.getFieldValue('month');
    const maKhoa = form.getFieldValue('maKhoa');
    const khoaName = khoaPhongs.find(k => k.makk === maKhoa)?.tenkk || 'Khoa';
    const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `BangCong_${khoaName}_${detailed ? 'ChiTiet_' : ''}Thang${month.format('MM_YYYY')}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportGroupExcel = () => {
    const html = generateTableHtml(false, true, undefined, true, true);
    const month = form.getFieldValue('month');
    const maKhoa = form.getFieldValue('maKhoa');
    const khoaName = khoaPhongs.find(k => k.makk === maKhoa)?.tenkk || 'Khoa';
    const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `BangCong_TD_${khoaName}_Thang${month.format('MM_YYYY')}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAllExcel = async () => {
    const month = form.getFieldValue('month');
    if (!month) { message.error('Vui lòng chọn tháng'); return; }
    setLoading(true);
    message.loading({ content: 'Đang tải dữ liệu toàn viện...', key: 'exportAll' });
    try {
      const res = await chamCongService.getBangCong(0, month.month() + 1, month.year());
      const html = generateTableHtml(true, true, res.data);
      const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `BangCong_ToanVien_ChiTiet_Thang${month.format('MM_YYYY')}.xls`;
      link.click();
      URL.revokeObjectURL(url);
      message.success({ content: 'Xuất dữ liệu toàn viện thành công', key: 'exportAll' });
    } catch (error) {
      message.error({ content: 'Lỗi khi xuất dữ liệu toàn viện', key: 'exportAll' });
    } finally {
      setLoading(false);
    }
  };

  const handleTinhLuong = async () => {
    const maKhoa = form.getFieldValue('maKhoa');
    const month = form.getFieldValue('month');
    if (!maKhoa || !month) {
      message.warning('Vui lòng chọn khoa và tháng trước khi tính lương');
      return;
    }

    setLuongLoading(true);
    try {
      const userRes = await authService.getProfile();
      const res = await luongService.tinhLuongKhoa({
        maKhoa,
        thang: month.month() + 1,
        nam: month.year(),
        nguoiTao: userRes.macc || 'system'
      });
      setBangLuongData(res.data);
      message.success('Tính lương thành công!');
    } catch (error) {
      message.error('Lỗi khi tính lương');
    } finally {
      setLuongLoading(false);
    }
  };

  const handleLoadBangLuong = async () => {
    const maKhoa = form.getFieldValue('maKhoa');
    const month = form.getFieldValue('month');
    if (!maKhoa || !month) {
      message.warning('Vui lòng chọn khoa và tháng trước khi xem bảng lương');
      return;
    }

    setLuongLoading(true);
    try {
      const res = await luongService.getBangLuong(maKhoa, month.month() + 1, month.year());
      setBangLuongData(res.data);
      if (res.data.length === 0) {
        message.info('Chưa có bảng lương cho kỳ này. Vui lòng tính lương trước.');
      }
    } catch (error) {
      message.error('Lỗi khi tải bảng lương');
    } finally {
      setLuongLoading(false);
    }
  };

  // Handlers CRUD cho LuongNhanVien
  const handleLoadLuongNhanVien = async () => {
    const maKhoa = form.getFieldValue('maKhoa');
    if (!maKhoa) {
      message.warning('Vui lòng chọn khoa trước');
      return;
    }
    setLuongLoading(true);
    try {
      const res = await luongService.getAllLuongNhanVien(maKhoa);
      setLuongNhanVienList(res.data);
    } catch (error) {
      message.error('Lỗi khi tải danh sách lương nhân viên');
    } finally {
      setLuongLoading(false);
    }
  };

  const handleOpenLuongNhanVienModal = (record?: any) => {
    if (record) {
      luongNhanVienForm.setFieldsValue({
        maChamCong: record.maChamCong,
        nguoiPhuThuoc: record.nguoiPhuThuoc,
        luongBHXH: record.luongBHXH,
        luongNS: record.luongNS,
        phuCapTN: record.phuCapTN,
        tnKhac: record.tnKhac,
        phuCapXX: record.phuCapXX
      });
    } else {
      luongNhanVienForm.resetFields();
    }
    setLuongNhanVienModalVisible(true);
  };

  const handleSaveLuongNhanVien = async () => {
    try {
      const values = await luongNhanVienForm.validateFields();
      await luongService.updateLuongNhanVien(values);
      message.success('Cập nhật thông tin lương nhân viên thành công');
      setLuongNhanVienModalVisible(false);
      handleLoadLuongNhanVien();
    } catch (error) {
      message.error('Lỗi khi cập nhật thông tin lương nhân viên');
    }
  };

  // Filter danh sách lương nhân viên
  const filteredLuongNhanVienList = useMemo(() => {
    return luongNhanVienList.filter(item => {
      const matchName = !luongNhanVienFilterName || 
        item.tenNV?.toLowerCase().includes(luongNhanVienFilterName.toLowerCase()) ||
        item.maChamCong?.toLowerCase().includes(luongNhanVienFilterName.toLowerCase());
      return matchName;
    });
  }, [luongNhanVienList, luongNhanVienFilterName]);

  // Handlers CRUD cho LuongConfig
  const handleLoadLuongConfig = async () => {
    setLuongLoading(true);
    try {
      const res = await luongService.getLuongConfig();
      setLuongConfigData(res.data);
      luongConfigForm.setFieldsValue(res.data);
    } catch (error) {
      message.error('Lỗi khi tải cấu hình lương');
    } finally {
      setLuongLoading(false);
    }
  };

  const handleOpenLuongConfigModal = () => {
    if (luongConfigData) {
      luongConfigForm.setFieldsValue(luongConfigData);
    }
    setLuongConfigModalVisible(true);
  };

  const handleSaveLuongConfig = async () => {
    try {
      const values = await luongConfigForm.validateFields();
      await luongService.updateLuongConfig(values);
      message.success('Cập nhật cấu hình lương thành công');
      setLuongConfigModalVisible(false);
      handleLoadLuongConfig();
    } catch (error) {
      message.error('Lỗi khi cập nhật cấu hình lương');
    }
  };

  // Handlers CRUD cho TienPhuCapTruc
  const handleLoadTienPhuCapTruc = async () => {
    setLuongLoading(true);
    try {
      const res = await luongService.getTienPhuCapTruc();
      setTienPhuCapTrucList(res.data);
    } catch (error) {
      message.error('Lỗi khi tải danh sách tiền phụ cấp trực');
    } finally {
      setLuongLoading(false);
    }
  };

  const handleOpenTienPhuCapTrucModal = (record?: any) => {
    if (record) {
      tienPhuCapTrucForm.setFieldsValue({
        kyHieu: record.kyHieu,
        soTien: record.soTien
      });
    } else {
      tienPhuCapTrucForm.resetFields();
    }
    setTienPhuCapTrucModalVisible(true);
  };

  const handleSaveTienPhuCapTruc = async () => {
    try {
      const values = await tienPhuCapTrucForm.validateFields();
      await luongService.updateTienPhuCapTruc(values);
      message.success('Cập nhật tiền phụ cấp trực thành công');
      setTienPhuCapTrucModalVisible(false);
      handleLoadTienPhuCapTruc();
    } catch (error) {
      message.error('Lỗi khi cập nhật tiền phụ cấp trực');
    }
  };

  const handleDeleteTienPhuCapTruc = async (kyHieu: string) => {
    modal.confirm({
      title: 'Xác nhận xóa',
      content: `Bạn có chắc chắn muốn xóa tiền phụ cấp trực "${kyHieu}" không?`,
      onOk: async () => {
        try {
          await luongService.deleteTienPhuCapTruc(kyHieu);
          message.success('Xóa tiền phụ cấp trực thành công');
          handleLoadTienPhuCapTruc();
        } catch (error) {
          message.error('Lỗi khi xóa tiền phụ cấp trực');
        }
      }
    });
  };

  // Handlers CRUD cho NgoaiLeTienTruc
  const handleLoadNgoaiLeTienTruc = async () => {
    const maKhoa = form.getFieldValue('maKhoa');
    if (!maKhoa) {
      message.warning('Vui lòng chọn khoa trước');
      return;
    }
    setLuongLoading(true);
    try {
      const res = await luongService.getNgoaiLeTienTruc(maKhoa);
      setNgoaiLeTienTrucList(res.data);
    } catch (error) {
      message.error('Lỗi khi tải danh sách ngoại lệ tiền trực');
    } finally {
      setLuongLoading(false);
    }
  };

  const handleOpenNgoaiLeTienTrucModal = (record?: any) => {
    if (record) {
      ngoaiLeTienTrucForm.setFieldsValue({
        maChamCong: record.maChamCong,
        maLoai: record.maLoai,
        soTien: record.soTien
      });
    } else {
      ngoaiLeTienTrucForm.resetFields();
    }
    setNgoaiLeTienTrucModalVisible(true);
  };

  const handleSaveNgoaiLeTienTruc = async () => {
    try {
      const values = await ngoaiLeTienTrucForm.validateFields();
      const userRes = await authService.getProfile();
      await luongService.updateNgoaiLeTienTruc({
        ...values,
        nguoiCapNhat: userRes.macc || 'system'
      });
      message.success('Cập nhật ngoại lệ tiền trực thành công');
      setNgoaiLeTienTrucModalVisible(false);
      handleLoadNgoaiLeTienTruc();
    } catch (error) {
      message.error('Lỗi khi cập nhật ngoại lệ tiền trực');
    }
  };

  const handleDeleteNgoaiLeTienTruc = async (id: number) => {
    modal.confirm({
      title: 'Xác nhận xóa',
      content: 'Bạn có chắc chắn muốn xóa ngoại lệ này không?',
      onOk: async () => {
        try {
          await luongService.deleteNgoaiLeTienTruc(id);
          message.success('Xóa ngoại lệ tiền trực thành công');
          handleLoadNgoaiLeTienTruc();
        } catch (error) {
          message.error('Lỗi khi xóa ngoại lệ tiền trực');
        }
      }
    });
  };

  const renderCell = useCallback((day: number, record: ChamCongRow) => {
    const cellKey = `${record.maChamCong}-${day}`;
    return (
      <TimekeepingCell
        day={day}
        record={record}
        canEdit={isCellEditable}
        isEditing={editingCell === cellKey}
        onStartEdit={() => setEditingCell(cellKey)}
        onEndEdit={() => setEditingCell(null)}
        viewingMonth={viewingMonth}
        dmLoai={dmLoai}
        dmLoaiPhu={dmLoaiPhu}
        cauHinhCas={cauHinhCas}
        isSuggested={suggestedCells.has(cellKey)}
        onValueChange={handleCellChange}
        onSecondaryValueChange={handleSecondaryCellChange}
      />
    );
  }, [isCellEditable, editingCell, viewingMonth, dmLoai, dmLoaiPhu, cauHinhCas, suggestedCells, handleCellChange, handleSecondaryCellChange]);

  const columns: any[] = useMemo(() => {
    if (data.length === 0) return [];
    const prevMonthDate = viewingMonth.subtract(1, 'month');
    const daysInPrevMonth = prevMonthDate.daysInMonth();
    const cols: any[] = [
      {
        title: 'Nhân viên',
        dataIndex: 'tenNV',
        key: 'tenNV',
        fixed: 'left',
        width: 140,
        render: (text: string, record: ChamCongRow) => (
          <div>
            <Text strong style={{ fontSize: '12px' }}>{text}</Text>
            <br/>
            <Text type="secondary" style={{ fontSize: '10px' }}>{record.maChamCong}</Text>
          </div>
        )
      }
    ];

    const dayLabels = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
    for (let i = 26; i <= daysInPrevMonth; i++) {
      const date = prevMonthDate.date(i);
      cols.push({
        title: <div style={{ fontSize: '11px', lineHeight: '1.1', color: '#8c8c8c' }}>{i}<br/>{dayLabels[date.day()]}</div>,
        key: `prev-${i}`,
        width: 75,
        align: 'center',
        render: (_: any, record: ChamCongRow) => renderCell(i, record)
      });
    }
    for (let i = 1; i <= 25; i++) {
      const date = viewingMonth.date(i);
      cols.push({
        title: <div style={{ fontSize: '11px', lineHeight: '1.1' }}>{i}<br/>{dayLabels[date.day()]}</div>,
        key: `curr-${i}`,
        width: 75,
        align: 'center',
        render: (_: any, record: ChamCongRow) => renderCell(i, record)
      });
    }

    cols.push(
      { title: <div style={{ fontSize: '10px', lineHeight: '1.1' }}>Công đi làm</div>, dataIndex: 'tongNgayDiLam', key: 'tongNgayDiLam', width: 45, fixed: 'right', align: 'center', render: (val: number) => <Text style={{ fontSize: '10px' }}>{val}</Text> },
      { title: <div style={{ fontSize: '10px', lineHeight: '1.1' }}>Công hưởng lương</div>, dataIndex: 'tongHuongLuong', key: 'tongHuongLuong', width: 45, fixed: 'right', align: 'center', render: (val: number) => <Text strong style={{ fontSize: '10px', color: '#389e0d' }}>{val}</Text> },
      { title: <div style={{ fontSize: '10px', lineHeight: '1.1' }}>Công hưởng BHXH</div>, dataIndex: 'tongBHXH', key: 'tongBHXH', width: 50, fixed: 'right', align: 'center', render: (val: number) => <Text style={{ fontSize: '10px', color: '#722ed1' }}>{val}</Text> },
      { title: <div style={{ fontSize: '10px', lineHeight: '1.1' }}>Tổng trực</div>, dataIndex: 'tongNgayTruc', key: 'tongNgayTruc', width: 45, fixed: 'right', align: 'center', render: (val: number) => <Text style={{ fontSize: '10px' }}>{val}</Text> },
      { title: <div style={{ fontSize: '10px', lineHeight: '1.1' }}>Nghỉ có lương</div>, dataIndex: 'tongNghiCoLuong', key: 'tongNghiCoLuong', width: 45, fixed: 'right', align: 'center', render: (val: number) => <Text style={{ fontSize: '10px' }}>{val}</Text> },
      { title: <div style={{ fontSize: '10px', lineHeight: '1.1' }}>Tồn nghỉ bù</div>, dataIndex: 'nghiBuLuyKe', key: 'nghiBuLuyKe', width: 50, fixed: 'right', align: 'center', render: (val: number) => (<span style={{ fontSize: '10px', fontWeight: 'bold', color: val >= 0 ? '#52c41a' : '#f5222d' }}>{val}</span>) },
      { title: <div style={{ fontSize: '10px', lineHeight: '1.1' }}>Tồn phép</div>, dataIndex: 'nghiPhepLuyKe', key: 'nghiPhepLuyKe', width: 50, fixed: 'right', align: 'center', render: (val: number) => (<span style={{ fontSize: '10px', fontWeight: 'bold', color: val >= 0 ? '#1890ff' : '#fa541c' }}>{val}</span>) }
    );
    return cols;
  }, [data, viewingMonth, renderCell]);

  return (
    <Card bodyStyle={{ padding: isMobile ? '8px' : '24px' }}>
      {contextHolder}
      <div style={{ marginBottom: 16 }}>
        <Title level={4}><ClockCircleOutlined /> Chấm công khoa phòng</Title>
        <Form form={form} layout="vertical" onFinish={handleSearch} initialValues={{ month: defaultMonth }}>
          <Row gutter={[12, 8]} style={{ marginBottom: 0 }}>
            {/* Bộ lọc + Nút hành động chính (luôn hiển thị) */}
            {/* TẤT CẢ 3 Col cùng style flex-end: đáy control (Select/Buttons) sẽ thẳng hàng tự động do stretch chiều cao bằng nhau */}
            <Col xs={24} sm={12} md={7} lg={5} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Form.Item name="maKhoa" label="Khoa" rules={[{ required: true }]} style={{ marginBottom: 0, width: '100%' }}>
                <Select size="middle" placeholder="Chọn khoa phòng" showSearch optionFilterProp="children" style={{ width: '100%' }}>
                  {khoaPhongs.map(k => (<Option key={k.makk} value={k.makk}>{k.tenkk}</Option>))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={5} lg={3} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Form.Item name="month" label="Tháng" rules={[{ required: true }]} style={{ marginBottom: 0, width: '100%' }}>
                <DatePicker size="middle" picker="month" format="MM/YYYY" allowClear={false} style={{ width: '100%' }} inputReadOnly />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={16} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
                {/* 4 Nút CHÍNH thường xuyên nhất (không gom) */}
                <Button type="primary" icon={<ReloadOutlined />} htmlType="submit" loading={loading}>Lấy dữ liệu</Button>
                
                {isPastMonth && !isManualUnlocked ? (
                  <Button 
                    danger 
                    icon={<LockOutlined />} 
                    disabled={!canEditCurrent}
                    onClick={() => {
                      modal.confirm({
                        title: 'Xác nhận mở khóa dữ liệu QUÁ KHỨ',
                        content: 'Bạn đang thao tác trên dữ liệu đã chốt chu kỳ. Việc chỉnh sửa có thể làm thay đổi số dư lũy kế của các tháng sau đó. Bạn có chắc chắn muốn mở khóa để hiệu chỉnh?',
                        okText: 'Tôi xác nhận, mở khóa ngay',
                        cancelText: 'Hủy',
                        onOk: () => setIsManualUnlocked(true)
                      });
                    }}
                  >
                    Mở khóa hiệu chỉnh
                  </Button>
                ) : (
                  <Button 
                    type="primary" 
                    icon={<SaveOutlined />} 
                    onClick={handleSave} 
                    loading={loading} 
                    disabled={isDataOutdated || !canEditCurrent}
                  >
                    Lưu bảng công
                  </Button>
                )}

                {/* ================= DROPDOWN 1: Gom 6 nút Thao tác chuyên sâu ================= */}
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      ...(hasConfigPerm ? [{
                        key: 'syncHIS',
                        icon: <SyncOutlined />,
                        label: 'Đồng bộ HIS',
                        disabled: (isPastMonth && !isManualUnlocked) || isDataOutdated || !canEditCurrent,
                        onClick: () => { const mK = form.getFieldValue('maKhoa'); if (mK) { loadSyncEmployees(mK); syncForm.setFieldsValue({ maChamCongs: [] }); setSyncModalVisible(true); } else { message.warning('Vui lòng chọn khoa phòng trước'); } }
                      }] : []),
                      {
                        key: 'suggest',
                        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                        label: 'Gợi ý công tự động',
                        disabled: (isPastMonth && !isManualUnlocked) || isDataOutdated || !canEditCurrent,
                        onClick: handleSuggestAutoFill
                      },
                      {
                        key: 'chedo',
                        icon: <InfoCircleOutlined />,
                        label: 'Chế độ nhân viên',
                        disabled: (isPastMonth && !isManualUnlocked) || isDataOutdated || !canEditCurrent,
                        onClick: () => { const mK = form.getFieldValue('maKhoa'); if (mK) { loadSyncEmployees(mK); chedoForm.resetFields(); chedoForm.setFieldsValue({ isContinuous: true }); setChedoModalVisible(true); } else { message.warning('Vui lòng chọn khoa phòng trước'); } }
                      },
                      ...(hasConfigPerm ? [{
                        key: 'reset',
                        icon: <RestOutlined />,
                        label: 'Reset số dư',
                        disabled: (isPastMonth && !isManualUnlocked) || isDataOutdated,
                        onClick: () => { const mK = form.getFieldValue('maKhoa'); if (mK) { loadSyncEmployees(mK); resetForm.setFieldsValue({ maChamCongs: data.map(i => i.maChamCong), ngayBatDau: dayjs().startOf('year'), isDefault: true }); setResetModalVisible(true); } else { message.warning('Vui lòng chọn khoa phòng trước'); } }
                      }] : []),
                      ...(hasDieuChinhBuPhepPerm ? [
                        {
                          key: 'dieuchinh',
                          icon: <EditOutlined />,
                          label: 'Điều chỉnh nghỉ bù/phép',
                          onClick: () => { 
                            const mK = form.getFieldValue('maKhoa'); 
                            if (mK) { 
                              loadSyncEmployees(mK); 
                              setSelectedEmployeeForDieuChinh(null);
                              setSoDuForDieuChinh(null);
                              setLoaiNgayForDieuChinh('NghiBu');
                              setSoNgayForDieuChinh(0);
                              setLyDoForDieuChinh('');
                              setDieuChinhModalVisible(true); 
                            } else { 
                              message.warning('Vui lòng chọn khoa phòng trước'); 
                            } 
                          }
                        },
                        {
                          key: 'vayhoancong',
                          icon: <SyncOutlined />,
                          label: 'Vay / Hoàn công',
                          onClick: () => {
                            const mK = form.getFieldValue('maKhoa');
                            if (mK) {
                              loadSyncEmployees(mK);
                              setSelectedEmployeeForVayHoan(null);
                              setVayHoanCurrentRecord(null);
                              setVayHoanTuThang(null);
                              setVayHoanSoNgay(1);
                              setVayHoanLyDo('');
                              setVayHoanModalVisible(true);
                            } else {
                              message.warning('Vui lòng chọn khoa phòng trước');
                            }
                          }
                        }
                      ] : [])
                    ]
                  }}
                >
                  <Button icon={<SettingOutlined />}>
                    Thao tác <DownOutlined style={{ fontSize: '10px' }} />
                  </Button>
                </Dropdown>

                {/* ================= DROPDOWN 2: Gom 4-5 nút In / Xuất báo cáo ================= */}
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      {
                        key: 'print',
                        icon: <PrinterOutlined />,
                        label: 'In bảng công',
                        disabled: isDataOutdated,
                        onClick: handlePrint
                      },
                      {
                        key: 'excel',
                        icon: <FileExcelOutlined />,
                        label: 'Xuất Excel',
                        disabled: isDataOutdated,
                        onClick: () => handleExportExcel(false)
                      },
                      {
                        key: 'excel_chitiet',
                        icon: <FileExcelOutlined />,
                        label: 'Xuất Excel chi tiết',
                        disabled: isDataOutdated,
                        onClick: () => handleExportExcel(true)
                      },
                      {
                        key: 'excel_td',
                        icon: <FileExcelOutlined style={{ color: '#fa8c16' }} />,
                        label: <span style={{ color: '#fa8c16', fontWeight: 500 }}>Xuất Excel Tập đoàn</span>,
                        disabled: isDataOutdated,
                        onClick: handleExportGroupExcel
                      },
                      ...(canViewAll ? [{
                        key: 'excel_all',
                        icon: <FileExcelOutlined />,
                        label: 'Xuất Excel toàn viện',
                        onClick: handleExportAllExcel
                      }] : [])
                    ]
                  }}
                >
                  <Button icon={<ExportOutlined />} style={{ backgroundColor: !isDataOutdated ? '#217346' : undefined, color: !isDataOutdated ? 'white' : undefined }}>
                    Xuất <DownOutlined style={{ fontSize: '10px' }} />
                  </Button>
                </Dropdown>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
      <Alert 
        type={isDataOutdated && data.length > 0 ? "warning" : "info"} 
        showIcon 
        style={{ marginBottom: 16 }} 
        message={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {isDataOutdated && data.length > 0 ? (
              <span><WarningOutlined /> Dữ liệu chưa đồng bộ. Vui lòng bấm <b>"Lấy dữ liệu"</b> trước khi thực hiện các thao tác.</span>
            ) : (
              <span>Ô đỏ: Thiếu vân tay. Bù và Phép được tính lũy kế từ tháng khởi tạo.</span>
            )}
            {dmChedo.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px', fontSize: '14px' }}>
                <Text strong>Chế độ:</Text>
                {dmChedo.map(c => (
                  <span key={c.maChedo} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '12px', height: '12px', backgroundColor: c.mauNhanDien, border: '1px solid #000000', borderRadius: '2px' }}></div>
                    <span>{c.tenChedo}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        } 
      />
      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        items={[
          {
            key: 'chamcong',
            label: 'Bảng công',
            children: (
              <Table dataSource={data} columns={columns} rowKey="maChamCong" loading={loading} pagination={false} scroll={{ x: 'max-content', y: 'calc(100vh - 350px)' }} bordered size="small" />
            )
          },
          ...(hasTinhLuongPerm ? [{
            key: 'luong',
            label: 'Tính lương',
            children: (
              <div>
                <Space style={{ marginBottom: 16 }}>
                  <Button type="primary" onClick={handleTinhLuong} loading={luongLoading}>Tính lương kỳ này</Button>
                  <Button icon={<ReloadOutlined />} onClick={handleLoadBangLuong} loading={luongLoading} />
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'config',
                          icon: <SettingOutlined />,
                          label: 'Cấu hình lương',
                          onClick: handleOpenLuongConfigModal
                        },
                        {
                          key: 'tienphuctruc',
                          icon: <DollarOutlined />,
                          label: 'Tiền phụ cấp trực',
                          onClick: handleOpenTienPhuCapTrucModal
                        },
                        {
                          key: 'ngoaiLeTienTruc',
                          icon: <DollarOutlined />,
                          label: 'Ngoại lệ tiền trực',
                          onClick: handleOpenNgoaiLeTienTrucModal
                        }
                      ]
                    }}
                  >
                    <Button icon={<SettingOutlined />}>Cấu hình</Button>
                  </Dropdown>
                </Space>
                {bangLuongData.length > 0 && (
                  <Table
                    dataSource={bangLuongData}
                    columns={[
                      { title: 'Mã CC', dataIndex: 'maChamCong', key: 'maChamCong', width: 100 },
                      { title: 'Tên NV', dataIndex: 'tenNV', key: 'tenNV', width: 150 },
                      { title: 'Số ngày hưởng lương', dataIndex: 'soNgayHuongLuong', key: 'soNgayHuongLuong', width: 120, align: 'right' },
                      { title: 'Lương cơ bản', dataIndex: 'luongCoBan', key: 'luongCoBan', width: 120, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                      { title: 'Phụ cấp TN', dataIndex: 'tienPhuCapTN', key: 'tienPhuCapTN', width: 100, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                      { title: 'Phụ cấp XX', dataIndex: 'tienPhuCapXX', key: 'tienPhuCapXX', width: 100, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                      { title: 'Tiền trực', dataIndex: 'tienTruc', key: 'tienTruc', width: 100, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                      { title: 'TN khác', dataIndex: 'tnKhac', key: 'tnKhac', width: 100, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                      { title: 'Giảm trừ BHXH', dataIndex: 'tienGiamTruBHXH', key: 'tienGiamTruBHXH', width: 120, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                      { title: 'Thuế TNCN', dataIndex: 'tienThueTNCN', key: 'tienThueTNCN', width: 100, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                      { title: 'Thực lĩnh', dataIndex: 'luongThucLinh', key: 'luongThucLinh', width: 120, align: 'right', render: (v) => <strong>{v.toLocaleString('vi-VN')}</strong> },
                    ]}
                    rowKey="maChamCong"
                    pagination={false}
                    scroll={{ x: 'max-content', y: 'calc(100vh - 450px)' }}
                    bordered
                    size="small"
                  />
                )}
              </div>
            )
          },
          {
            key: 'luongnhanvien',
            label: 'Lương nhân viên',
            children: (
              <div>
                <Space style={{ marginBottom: 16 }}>
                  <Button onClick={handleLoadLuongNhanVien} loading={luongLoading}>Tải danh sách</Button>
                  <Button type="primary" onClick={() => handleOpenLuongNhanVienModal()}>Thêm mới</Button>
                  <Input
                    placeholder="Tìm theo tên hoặc mã CC"
                    style={{ width: 250 }}
                    value={luongNhanVienFilterName}
                    onChange={(e) => setLuongNhanVienFilterName(e.target.value)}
                    allowClear
                  />
                </Space>
                <Table
                  dataSource={filteredLuongNhanVienList}
                  columns={[
                    { title: 'Mã CC', dataIndex: 'maChamCong', key: 'maChamCong', width: 100 },
                    { title: 'Tên NV', dataIndex: 'tenNV', key: 'tenNV', width: 150 },
                    { title: 'Người phụ thuộc', dataIndex: 'nguoiPhuThuoc', key: 'nguoiPhuThuoc', width: 120, align: 'center' },
                    { title: 'Lương BHXH', dataIndex: 'luongBHXH', key: 'luongBHXH', width: 120, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                    { title: 'Lương NS', dataIndex: 'luongNS', key: 'luongNS', width: 120, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                    { title: 'Phụ cấp TN', dataIndex: 'phuCapTN', key: 'phuCapTN', width: 120, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                    { title: 'TN khác', dataIndex: 'tnKhac', key: 'tnKhac', width: 100, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                    { title: 'Phụ cấp XX', dataIndex: 'phuCapXX', key: 'phuCapXX', width: 100, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
                    {
                      title: 'Thao tác',
                      key: 'action',
                      width: 100,
                      render: (_, record) => (
                        <Button type="link" size="small" onClick={() => handleOpenLuongNhanVienModal(record)}>Sửa</Button>
                      )
                    }
                  ]}
                  rowKey="maChamCong"
                  pagination={{
                    current: luongNhanVienPagination.current,
                    pageSize: luongNhanVienPagination.pageSize,
                    total: filteredLuongNhanVienList.length,
                    onChange: (page, pageSize) => setLuongNhanVienPagination({ current: page, pageSize: pageSize || 20 })
                  }}
                  scroll={{ x: 'max-content', y: 'calc(100vh - 450px)' }}
                  bordered
                  size="small"
                />
              </div>
            )
          }] : [])
        ]}
      />
      
      <Modal 
        title="Quản lý chế độ nhân viên" 
        open={chedoModalVisible} 
        onCancel={() => setChedoModalVisible(false)} 
        onOk={() => chedoForm.submit()}
        confirmLoading={chedoLoading}
        width={600}
      >
        <Form form={chedoForm} layout="vertical" onFinish={handleSaveChedo}>
          <Form.Item name="id" hidden><Input /></Form.Item>
          <Form.Item name="maChamCong" label="Chọn nhân viên" rules={[{ required: true }]}>
            <Select placeholder="Chọn nhân viên">
              {syncEmployees.map(emp => (<Option key={emp.macc} value={emp.macc}>{emp.tennv} ({emp.macc})</Option>))}
            </Select>
          </Form.Item>
          <Form.Item name="maChedo" label="Loại chế độ" rules={[{ required: true }]}>
            <Select placeholder="Chọn loại chế độ">
              {dmChedo.map(c => (<Option key={c.maChedo} value={c.maChedo}>{c.tenChedo} (Giảm {c.giamGioLamMinutes}p)</Option>))}
            </Select>
          </Form.Item>
          <Form.Item name="isContinuous" label="Hình thức áp dụng" valuePropName="checked">
            <Checkbox>Áp dụng liên tục (Hàng ngày trong khoảng)</Checkbox>
          </Form.Item>
          
          {chedoIsContinuous ? (
            <Form.Item name="range" label="Khoảng thời gian" rules={[{ required: true }]}>
              <DatePicker.RangePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          ) : (
            <Form.Item name="discreteDates" label="Chọn các ngày cụ thể" rules={[{ required: true }]}>
              <DatePicker multiple format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Chọn các ngày" />
            </Form.Item>
          )}

          <Form.Item name="ghiChu" label="Ghi chú">
            <TextArea rows={2} placeholder="Nhập ghi chú nếu có..." />
          </Form.Item>
        </Form>

        {currentNhanVienChedos.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Divider orientation="left">Chế độ trong chu kỳ</Divider>
            <Table
              size="small"
              dataSource={currentNhanVienChedos}
              pagination={false}
              rowKey="id"
              columns={[
                {
                  title: 'Loại chế độ',
                  dataIndex: ['chedo', 'tenChedo'],
                  key: 'tenChedo',
                  render: (text: string, record: any) => <Tag color={record.chedo?.mauNhanDien}>{text}</Tag>
                },
                {
                  title: 'Thời gian',
                  key: 'period',
                  render: (_: any, record: any) => record.isContinuous 
                    ? `${dayjs(record.tuNgay).format('DD/MM/YYYY')} - ${dayjs(record.denNgay).format('DD/MM/YYYY')}`
                    : `Ngày lẻ (${record.details?.length || 0} ngày)`
                },
                {
                  title: 'Thao tác',
                  key: 'action',
                  width: 100,
                  render: (_: any, record: any) => (
                    <Space>
                      <Button 
                        type="text" 
                        icon={<SyncOutlined style={{ color: '#1890ff' }} />} 
                        onClick={() => handleEditChedo(record)} 
                        title="Sửa"
                      />
                      <Button 
                        type="text" 
                        danger 
                        icon={<RestOutlined />} 
                        onClick={() => handleDeleteChedo(record.id)} 
                        title="Xóa"
                      />
                    </Space>
                  )
                }
              ]}
            />
          </div>
        )}
      </Modal>

      <Modal title="Đồng bộ dữ liệu chấm công từ HIS" open={syncModalVisible} onCancel={() => setSyncModalVisible(false)} confirmLoading={syncLoading} width={600} forceRender footer={[<Button key="cancel" onClick={() => setSyncModalVisible(false)}>Hủy</Button>, hasConfigPerm && (<Button key="syncAll" danger icon={<SyncOutlined />} onClick={handleSyncAllEmployees} loading={syncLoading}>Đồng bộ tất cả NV (Toàn viện)</Button>), <Button key="submit" type="primary" onClick={() => syncForm.submit()} loading={syncLoading}>Đồng bộ theo khoa</Button>]}>
        <div style={{ marginBottom: 16 }}><Alert message={<span>Đang thực hiện đồng bộ cho <b>{khoaPhongs.find(k => k.makk === form.getFieldValue('maKhoa'))?.tenkk}</b> tháng <b>{form.getFieldValue('month')?.format('MM/YYYY')}</b></span>} type="info" showIcon /></div>
        <Form form={syncForm} layout="vertical" onFinish={handleSyncFromHis}>
          <Form.Item name="maChamCongs" label="Chọn nhân viên cụ thể (để trống nếu muốn đồng bộ toàn khoa)">
            <Checkbox.Group style={{ width: '100%' }}><div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #f0f0f0', padding: '8px' }}><Row>{syncEmployees.map(emp => (<Col span={12} key={emp.macc}><Checkbox value={emp.macc}>{emp.tennv}</Checkbox></Col>))}</Row></div></Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
      <Modal title="Reset số dư khởi tạo (Nghỉ bù/phép)" open={resetModalVisible} onCancel={() => setResetModalVisible(false)} onOk={() => resetForm.submit()} confirmLoading={resetLoading} width={600} forceRender>
        <Form form={resetForm} layout="vertical" onFinish={handleResetInit} initialValues={{ isDefault: true }}>
          <Form.Item name="ngayBatDau" label="Ngày bắt đầu chu kỳ mới (Reset)" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" inputReadOnly /></Form.Item>
          <Form.Item name="maChamCongs" label="Chọn nhân viên" rules={[{ required: true, message: 'Vui lòng chọn ít nhất 1 NV' }]}><Select mode="multiple" placeholder="Chọn nhân viên" style={{ width: '100%' }}>{syncEmployees.map(emp => (<Option key={emp.macc} value={emp.macc}>{emp.tennv} ({emp.macc})</Option>))}</Select></Form.Item>
          <Form.Item name="isDefault" valuePropName="checked"><Checkbox><b>Reset mặc định theo thâm niên</b><div style={{ fontSize: '12px', color: '#8c8c8c', marginLeft: 24 }}>(Cứ 5 năm làm việc được cộng 1 ngày phép thâm niên. Nghỉ bù về 0. Nếu NV chưa có ngày vào làm, phép gốc = 0)</div></Checkbox></Form.Item>
        </Form>
      </Modal>
      
      {/* Modal Điều chỉnh nghỉ bù/phép */}
      <Modal 
        title="Điều chỉnh nghỉ bù/phép" 
        open={dieuChinhModalVisible} 
        onCancel={() => setDieuChinhModalVisible(false)} 
        onOk={handleDieuChinhSave}
        confirmLoading={dieuChinhModalLoading}
        width={600}
      >
        <Spin spinning={dieuChinhModalLoading}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Chọn Nhân viên */}
            <div>
              <Text strong>Nhân viên <Text type="danger">*</Text></Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Chọn nhân viên"
                value={selectedEmployeeForDieuChinh}
                onChange={handleDieuChinhEmployeeChange}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {syncEmployees.map(emp => (
                  <Option key={emp.macc} value={emp.macc}>
                    {emp.tennv} ({emp.macc})
                  </Option>
                ))}
              </Select>
            </div>

            {/* Hiển thị số dư gốc */}
            {soDuForDieuChinh && (
              <Card size="small" style={{ backgroundColor: '#f5f5f5' }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="Nghỉ bù gốc"
                      value={soDuForDieuChinh.nghiBuGoc || 0}
                      precision={4}
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Nghỉ phép gốc"
                      value={soDuForDieuChinh.nghiPhepGoc || 0}
                      precision={4}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                </Row>
              </Card>
            )}

            {/* Chọn loại ngày */}
            <div>
              <Text strong>Loại ngày <Text type="danger">*</Text></Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={loaiNgayForDieuChinh}
                onChange={(value) => setLoaiNgayForDieuChinh(value as 'NghiBu' | 'NghiPhep')}
                disabled={!soDuForDieuChinh}
              >
                <Option value="NghiBu">Nghỉ bù</Option>
                <Option value="NghiPhep">Nghỉ phép</Option>
              </Select>
            </div>

            {/* Nhập số ngày */}
            <div>
              <Text strong>Số ngày (điền số âm để trừ) <Text type="danger">*</Text></Text>
              <InputNumber
                style={{ width: '100%', marginTop: 8 }}
                value={soNgayForDieuChinh}
                onChange={(value) => setSoNgayForDieuChinh(value || 0)}
                step={0.5}
                precision={4}
                placeholder="Ví dụ: 1.5 hoặc -0.5"
                disabled={!soDuForDieuChinh}
              />
              {soDuForDieuChinh && (
                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                  Số sau khi điều chỉnh: {(loaiNgayForDieuChinh === 'NghiBu' ? (soDuForDieuChinh.nghiBuGoc || 0) : (soDuForDieuChinh.nghiPhepGoc || 0)) + (soNgayForDieuChinh || 0)}
                </Text>
              )}
            </div>

            {/* Lý do */}
            <div>
              <Text strong>Lý do <Text type="danger">*</Text></Text>
              <Input.TextArea
                style={{ width: '100%', marginTop: 8 }}
                value={lyDoForDieuChinh}
                onChange={(e) => setLyDoForDieuChinh(e.target.value)}
                placeholder="Nhập lý do điều chỉnh..."
                rows={3}
                disabled={!soDuForDieuChinh}
              />
            </div>
          </Space>
        </Spin>
      </Modal>

      {/* Modal Vay / Hoàn công */}
      <Modal 
        title="Vay / Hoàn công (điều chỉnh ngày hưởng lương giữa 2 kỳ liền kề)" 
        open={vayHoanModalVisible} 
        onCancel={() => setVayHoanModalVisible(false)} 
        onOk={handleVayHoanSave}
        confirmLoading={vayHoanModalLoading}
        width={600}
        forceRender
      >
        <Spin spinning={vayHoanModalLoading}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {vayHoanCurrentRecord && (
              <Alert 
                type="info" 
                showIcon 
                message={
                  <span>
                    <InfoCircleOutlined /> Kỳ này <b>ĐÃ CÓ</b> liên hệ #{vayHoanCurrentRecord.id} (tạo ngày {vayHoanCurrentRecord.ngayTao ? dayjs(vayHoanCurrentRecord.ngayTao).format('DD/MM/YYYY HH:mm') : ''}). 
                    Bạn đang <b>SỬA</b> liên hệ cũ (đặt số ngày = 0 để vô hiệu hóa).
                  </span>
                } 
              />
            )}

            {/* 1. Chọn Nhân viên */}
            <div>
              <Text strong>Nhân viên <Text type="danger">*</Text></Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Chọn nhân viên"
                value={selectedEmployeeForVayHoan}
                onChange={handleVayHoanEmployeeChange}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                disabled={vayHoanModalLoading}
              >
                {syncEmployees.map(emp => (
                  <Option key={emp.macc} value={emp.macc}>
                    {emp.tennv} ({emp.macc})
                  </Option>
                ))}
              </Select>
            </div>

            {/* 2. Từ tháng (nguồn) + Đến tháng (đích) */}
            <Row gutter={16}>
              <Col span={12}>
                <div>
                  <Text strong>Từ tháng (nguồn - TRỪ đi) <Text type="danger">*</Text></Text>
                  <DatePicker
                    style={{ width: '100%', marginTop: 8 }}
                    picker="month"
                    format="MM/YYYY"
                    inputReadOnly
                    value={vayHoanTuThang}
                    onChange={handleVayHoanTuThangChange}
                    disabled={vayHoanModalLoading || !selectedEmployeeForVayHoan}
                  />
                  <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: '11px' }}>
                    Kỳ NGUỒN: công hưởng lương sẽ bị TRỪ (-) số ngày điều chỉnh
                  </Text>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Text strong>Đến tháng (đích - CỘNG vào)</Text>
                  <DatePicker
                    style={{ width: '100%', marginTop: 8 }}
                    picker="month"
                    format="MM/YYYY"
                    inputReadOnly
                    value={vayHoanDenThang}
                    disabled
                  />
                  <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: '11px' }}>
                    Kỳ ĐÍCH: tự động = Từ tháng + 1 (LUÔN liền kề). Sẽ được CỘNG (+) số ngày
                  </Text>
                </div>
              </Col>
            </Row>

            {/* 3. Điều chỉnh (số ngày) */}
            <div>
              <Text strong>Điều chỉnh (số ngày hưởng lương: dương=Hoàn, âm=Vay, 0=hủy) <Text type="danger">*</Text></Text>
              <InputNumber
                style={{ width: '100%', marginTop: 8 }}
                value={vayHoanSoNgay}
                onChange={(value) => setVayHoanSoNgay(value ?? 0)}
                min={-2}
                max={2}
                step={0.5}
                precision={4}
                placeholder="Mặc định: 1 (chuyển 1 ngày công từ tháng nguồn sang tháng đích)"
                disabled={vayHoanModalLoading || !selectedEmployeeForVayHoan || !vayHoanTuThang}
                addonBefore={<span style={{ fontSize: '11px' }}>±2 ngày</span>}
              />
              <Text 
                type={vayHoanSoNgay === null || vayHoanSoNgay === undefined ? 'secondary' : (vayHoanSoNgay === 0 ? 'warning' : (vayHoanSoNgay > 0 ? 'success' : 'danger'))} 
                style={{ display: 'block', marginTop: 6, fontSize: '12px' }}
              >
                {vayHoanSoNgay === 0 
                  ? '⚠️ Đặt 0 = VÔ HIỆU HÓA liên hệ này (nếu đã có cũ)'
                  : vayHoanSoNgay && vayHoanSoNgay > 0 
                    ? `✅ HOÀN CÔNG: Kỳ nguồn TRỪ ${vayHoanSoNgay} ngày → Kỳ đích CỘNG ${vayHoanSoNgay} ngày hưởng lương`
                    : vayHoanSoNgay && vayHoanSoNgay < 0 
                      ? `📉 VAY CÔNG (âm): Kỳ nguồn CỘNG ${Math.abs(vayHoanSoNgay)} ngày → Kỳ đích TRỪ ${Math.abs(vayHoanSoNgay)} ngày hưởng lương`
                      : 'Nhập số ngày dương (hoàn) hoặc âm (vay), giới hạn ±2 ngày'
                }
              </Text>
            </div>

            {/* 4. Lý do */}
            <div>
              <Text strong>Lý do <Text type="danger">*</Text></Text>
              <Input.TextArea
                style={{ width: '100%', marginTop: 8 }}
                value={vayHoanLyDo}
                onChange={(e) => setVayHoanLyDo(e.target.value)}
                placeholder="Nhập lý do vay/hoàn công..."
                rows={3}
                disabled={vayHoanModalLoading || !selectedEmployeeForVayHoan || !vayHoanTuThang}
              />
            </div>
          </Space>
        </Spin>
      </Modal>

      {/* Modal Lương nhân viên */}
      <Modal
        title="Thông tin lương nhân viên"
        open={luongNhanVienModalVisible}
        onCancel={() => setLuongNhanVienModalVisible(false)}
        onOk={handleSaveLuongNhanVien}
        width={600}
      >
        <Form form={luongNhanVienForm} layout="vertical">
          <Form.Item name="maChamCong" label="Mã chấm công" rules={[{ required: true }]}>
            <Select placeholder="Chọn nhân viên" showSearch optionFilterProp="children">
              {data.map(emp => (<Option key={emp.maChamCong} value={emp.maChamCong}>{emp.tenNV} ({emp.maChamCong})</Option>))}
            </Select>
          </Form.Item>
          <Form.Item name="nguoiPhuThuoc" label="Người phụ thuộc" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="luongBHXH" label="Lương BHXH" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="luongNS" label="Lương năng suất" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="phuCapTN" label="Phụ cấp trách nhiệm">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="tnKhac" label="Thu nhập khác">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="phuCapXX" label="Phụ cấp (xăng xe, điện thoại)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Cấu hình lương */}
      <Modal
        title="Cấu hình lương"
        open={luongConfigModalVisible}
        onCancel={() => setLuongConfigModalVisible(false)}
        onOk={handleSaveLuongConfig}
        width={600}
      >
        <Form form={luongConfigForm} layout="vertical">
          <Form.Item name="giamTruCaNhan" label="Giảm trừ cá nhân" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="giamTruNguoiPhuThuoc" label="Giảm trừ người phụ thuộc" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="tyLeBHXH" label="% BHXH" rules={[{ required: true }]}>
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="tyLeBHYT" label="% BHYT" rules={[{ required: true }]}>
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="tyLeBHTN" label="% BHTN" rules={[{ required: true }]}>
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Tiền phụ cấp trực */}
      <Modal
        title="Tiền phụ cấp trực"
        open={tienPhuCapTrucModalVisible}
        onCancel={() => setTienPhuCapTrucModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setTienPhuCapTrucModalVisible(false)}>Đóng</Button>,
          <Button key="add" type="primary" onClick={() => { tienPhuCapTrucForm.resetFields(); }}>Thêm mới</Button>,
          <Button key="save" type="primary" onClick={handleSaveTienPhuCapTruc}>Lưu</Button>
        ]}
      >
        <Space style={{ marginBottom: 16 }}>
          <Button onClick={handleLoadTienPhuCapTruc} loading={luongLoading}>Tải danh sách</Button>
        </Space>
        <Table
          dataSource={tienPhuCapTrucList}
          columns={[
            { title: 'Ký hiệu', dataIndex: 'kyHieu', key: 'kyHieu', width: 100 },
            { title: 'Số tiền', dataIndex: 'soTien', key: 'soTien', width: 150, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
            {
              title: 'Thao tác',
              key: 'action',
              width: 120,
              render: (_, record) => (
                <Space>
                  <Button type="link" size="small" onClick={() => handleOpenTienPhuCapTrucModal(record)}>Sửa</Button>
                  <Button type="link" size="small" danger onClick={() => handleDeleteTienPhuCapTruc(record.kyHieu)}>Xóa</Button>
                </Space>
              )
            }
          ]}
          rowKey="kyHieu"
          pagination={false}
          scroll={{ x: 'max-content', y: 300 }}
          bordered
          size="small"
        />
        <Divider />
        <Form form={tienPhuCapTrucForm} layout="vertical">
          <Form.Item name="kyHieu" label="Ký hiệu" rules={[{ required: true }]}>
            <Input placeholder="Nhập ký hiệu (ví dụ: tr, T, HT...)" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="soTien" label="Số tiền" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Ngoại lệ tiền trực */}
      <Modal
        title="Ngoại lệ tiền trực"
        open={ngoaiLeTienTrucModalVisible}
        onCancel={() => setNgoaiLeTienTrucModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setNgoaiLeTienTrucModalVisible(false)}>Đóng</Button>,
          <Button key="add" type="primary" onClick={() => { ngoaiLeTienTrucForm.resetFields(); }}>Thêm mới</Button>,
          <Button key="save" type="primary" onClick={handleSaveNgoaiLeTienTruc}>Lưu</Button>
        ]}
      >
        <Space style={{ marginBottom: 16 }}>
          <Button onClick={handleLoadNgoaiLeTienTruc} loading={luongLoading}>Tải danh sách</Button>
        </Space>
        <Table
          dataSource={ngoaiLeTienTrucList}
          columns={[
            { title: 'Mã CC', dataIndex: 'maChamCong', key: 'maChamCong', width: 100 },
            { title: 'Tên NV', dataIndex: 'tenNV', key: 'tenNV', width: 150 },
            { title: 'Loại trực', dataIndex: 'maLoai', key: 'maLoai', width: 100 },
            { title: 'Số tiền', dataIndex: 'soTien', key: 'soTien', width: 150, align: 'right', render: (v) => v.toLocaleString('vi-VN') },
            {
              title: 'Thao tác',
              key: 'action',
              width: 100,
              render: (_, record) => (
                <Button type="link" size="small" danger onClick={() => handleDeleteNgoaiLeTienTruc(record.id)}>Xóa</Button>
              )
            }
          ]}
          rowKey="id"
          pagination={false}
          scroll={{ x: 'max-content', y: 300 }}
          bordered
          size="small"
        />
        <Divider />
        <Form form={ngoaiLeTienTrucForm} layout="vertical">
          <Form.Item name="maChamCong" label="Nhân viên" rules={[{ required: true }]}>
            <Select placeholder="Chọn nhân viên" showSearch optionFilterProp="children">
              {data.map(emp => (<Option key={emp.maChamCong} value={emp.maChamCong}>{emp.tenNV} ({emp.maChamCong})</Option>))}
            </Select>
          </Form.Item>
          <Form.Item name="maLoai" label="Loại trực" rules={[{ required: true }]}>
            <Input placeholder="Nhập loại trực (ví dụ: tr, T, HT...)" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="soTien" label="Số tiền" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ChamCongKhoaPhongTab;
